import React from 'react';
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, Clock, CalendarDays } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { BlogPost } from '@/services/blogService';

interface BlogPostCardProps {
  post: BlogPost;
  index: number;
  isLoading: boolean;
  onCategoryClick: (category: string) => void;
  onTagClick: (tag: string) => void;
  selectedTag: string | null;
}

const BlogPostCard: React.FC<BlogPostCardProps> = ({ 
  post, 
  index, 
  isLoading,
  onCategoryClick,
  onTagClick,
  selectedTag
}) => {
  return (
    <Card className={`card overflow-hidden hover:shadow-lg transition-all duration-300 flex flex-col h-full ${isLoading ? 'opacity-50' : 'opacity-100'}`}>
      <CardHeader className="pb-2">
        <div className="flex justify-between items-start mb-3">
          <Badge 
            className="bg-primary/10 text-primary hover:bg-primary/20 border-none transition-colors cursor-pointer"
            onClick={() => onCategoryClick(post.category)}
          >
            {post.category}
          </Badge>
          <div className="flex flex-col items-end sm:flex-row sm:items-center text-xs text-muted-foreground gap-1 sm:gap-3">
            <span className="flex items-center gap-1">
              <CalendarDays className="h-3 w-3" />
              {post.date}
            </span>
            <span className="hidden sm:inline">•</span>
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {post.readingTime}
            </span>
          </div>
        </div>
        <h2 className="text-xl font-bold leading-tight group">
          {post.isPublished ? (
            <Link to={`/blog/${post.id}`} className="hover:text-primary transition-colors">
              {post.title}
            </Link>
          ) : (
            <span className="text-muted-foreground">{post.title}</span>
          )}
        </h2>
      </CardHeader>
      
      <CardContent className="flex-grow">
        <p className="text-muted-foreground text-sm line-clamp-3 mb-4">
          {post.excerpt}
        </p>
        <div className="flex flex-wrap gap-1.5 mt-auto">
          {post.tags.map((tag, tagIndex) => (
            <Badge 
              key={tagIndex} 
              variant={selectedTag === tag ? "default" : "outline"} 
              className="text-xs cursor-pointer hover:bg-secondary/80 bg-transparent"
              onClick={() => onTagClick(tag)}
            >
              {tag}
            </Badge>
          ))}
        </div>
      </CardContent>

      <CardFooter className="pt-4 border-t border-border/40 mt-auto">
        {post.isPublished ? (
          <Button asChild variant="ghost" className="w-full justify-between hover:bg-primary/5 hover:text-primary group">
            <Link to={`/blog/${post.id}`}>
              Read Article
              <ArrowRight className="h-4 w-4 ml-2 transition-transform group-hover:translate-x-1" />
            </Link>
          </Button>
        ) : (
          <Button variant="ghost" disabled className="w-full justify-between opacity-50 cursor-not-allowed">
            Coming Soon
            <Clock className="h-4 w-4 ml-2" />
          </Button>
        )}
      </CardFooter>
    </Card>
  );
};

export default BlogPostCard;
